import React from 'react';
import Tooltip from '../Tooltip';
import LiveConnectionDot from '../LiveConnectionDot';
import RequestTabBadge from '../RequestTabBadge';
import { X, Circle, Braces, Database, Kanban, FileText, FolderKanban } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CollectionTabBadge from '../CollectionTabBadge';
import { getTabMethod, getTabTitle, getTabTooltip, isRequestTabCloseable, tooltipContent } from './tabStripModel';

export function TabCloseIcon({ unsaved, size = 11 }) {
  return unsaved
    ? <Circle size={size} fill="currentColor" stroke="none" strokeWidth={0} aria-hidden />
    : <X size={size} strokeWidth={2.5} aria-hidden />;
}

export function SortableTab({ id, pinned, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: pinned });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
      }}
      {...(pinned ? {} : attributes)}
      {...(pinned ? {} : listeners)}
    >
      {children}
    </div>
  );
}

export function TabComponent({
  tab, activeTabId, activeTabType, requestTabCount,
  onTabChange, onTabClose, onContextMenu, t,
}) {
  const active = activeTabId === tab.id && activeTabType === tab.type;
  const method = getTabMethod(tab);
  const title = getTabTitle(tab, t);
  const tooltip = getTabTooltip(tab, t);
  const isCloseable = isRequestTabCloseable(tab, requestTabCount);

  return (
    <div
      className={`flex items-center space-x-2 px-2 py-1 min-w-0 flex-shrink-0 cursor-pointer transition-colors duration-150 etm-tab ${
        active ? 'etm-tab--active' : 'etm-tab--inactive'
      }`}
      style={{ maxWidth: 180, overflow: 'hidden' }}
      data-tab-active={active ? 'true' : undefined}
      onClick={() => onTabChange(tab.id, tab.type)}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(e.clientX, e.clientY, tab);
      }}
    >
      {/* Method Badge (for requests) or Type Badge (for collections/schemas) */}
      {tab.type === 'request' ? (
        <RequestTabBadge tab={tab} method={method} />
      ) : tab.type === 'collection' ? (
        <CollectionTabBadge tab={tab} />
      ) : tab.type === 'schema' ? (
        <Braces size={13} className="flex-shrink-0 text-sky-600" aria-label="Schema" />
      ) : tab.type === 'db-manager-table' ? (
        <Database size={13} className="flex-shrink-0 text-emerald-600" aria-label="Table" />
      ) : tab.type === 'task-manager-board' ? (
        <Kanban size={13} className="flex-shrink-0 text-indigo-600" aria-label="Board" />
      ) : tab.type === 'projects-project' ? (
        <FolderKanban size={13} className="flex-shrink-0 text-violet-600" aria-label="Project" />
      ) : tab.type === 'document-editor' ? (
        <FileText size={13} className="flex-shrink-0 text-violet-600" aria-label="Document" />
      ) : null}

      {/* Tab Title */}
      {/* The app's own Tooltip, never the native `title`. `aria-label` is set
          because Tooltip would otherwise name this span after its CONTENT — the
          URL — and a screen reader would lose the tab's actual title. */}
      <Tooltip content={tooltipContent(tooltip)} side="bottom" disabled={!tooltip}>
        <span
          className="text-xs font-medium truncate min-w-0 flex-1"
          aria-label={typeof title === 'string' ? title : undefined}
        >
          {title}
        </span>
      </Tooltip>
      {tab.type === 'request' && <LiveConnectionDot tabId={tab.id} />}

      {/* Close / unsaved indicator — always rendered to keep height stable */}
      <Tooltip
        content={tab.hasUnsavedChanges ? t('tabs.closeUnsaved') : t('tabs.closeTab')}
        side="bottom"
        disabled={!isCloseable}
      >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!isCloseable) return;
          onTabClose(tab.id, tab.type);
        }}
        className={`tab-bar-close-btn flex-shrink-0 cursor-pointer rounded-lg bg-transparent py-1 transition-colors ${
          tab.hasUnsavedChanges ? 'tab-bar-close-btn--unsaved' : ''
        }`}
        style={{ visibility: isCloseable ? 'visible' : 'hidden', pointerEvents: isCloseable ? undefined : 'none' }}
        aria-label={tab.hasUnsavedChanges ? t('tabs.closeUnsavedAria') : t('tabs.closeTabAria')}
      >
        <TabCloseIcon unsaved={tab.hasUnsavedChanges} size={11} />
      </button>
      </Tooltip>
    </div>
  );
}
